import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  CircularProgress,
  Box,
  Chip
} from '@mui/material';
import api from '../../services/useApi';

interface PonPortsOltSelectProps {
  oltId: number;
  value: string[] | string;
  onChange: (value: string[] | string) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  multiple?: boolean;
  renderValue?: (selected: string) => string;
  menuItemLabel?: (port: Port) => string;
}

interface Port {
  id: number;
  portNumber: string;
  sfp?: string;
  slot: number;
  status: string;
}

interface Card {
  id: number;
  slot: number;
  status: string;
}

const PonPortsOltSelect: React.FC<PonPortsOltSelectProps> = ({
  oltId,
  value,
  onChange,
  label = 'Porta PON',
  disabled = false,
  required = false,
  fullWidth = true,
  size = 'medium',
  multiple = true,
  renderValue,
  menuItemLabel
}) => {
  const [ports, setPorts] = useState<Port[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (oltId) {
      fetchPorts();
    } else {
      setPorts([]);
    }
  }, [oltId]);

  const fetchPorts = async () => {
    if (!oltId) return;

    setLoading(true);
    setError(null);

    try {
      // Buscar cards da OLT
      const cardsResponse = await api.get(`/olts/${oltId}/cards`);
      const cards: Card[] = cardsResponse.data.data || cardsResponse.data;

      console.log('Cards encontrados:', cards);

      if (!cards || cards.length === 0) {
        setPorts([]);
        return;
      }

      // Buscar portas de todos os cards
      const allPorts: Port[] = [];
      
      for (const card of cards) {
        try {
          const portsResponse = await api.get(`/olts/${oltId}/cards/${card.slot}/ports`);
          const cardPorts = (portsResponse.data.data || portsResponse.data) || [];
          
          console.log(`Portas do card slot ${card.slot}:`, cardPorts);
          
          // Mapear as portas (incluir sfp para uso como label)
          const mappedPorts = cardPorts.map((port: any) => ({
            id: port.id,
            portNumber: port.portNumber,
            sfp: port.sfp ?? `1/${card.slot}/${port.portNumber}`,
            slot: card.slot,
            status: port.status
          }));
          
          allPorts.push(...mappedPorts);
        } catch (error) {
          console.warn(`Erro ao buscar portas do card slot ${card.slot}:`, error);
        }
      }

      // Ordenar todas as portas por slot e depois por número da porta (ordem numérica)
      const sortedPorts = [...allPorts].sort((a, b) => {
        if (a.slot !== b.slot) return a.slot - b.slot;
        return (parseInt(a.portNumber, 10) || 0) - (parseInt(b.portNumber, 10) || 0);
      });

      setPorts(sortedPorts);
    } catch (error) {
      console.error('Erro ao buscar portas da OLT:', error);
      setError('Erro ao carregar portas');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: SelectChangeEvent<string[] | string>) => {
    const value = event.target.value;
    if (multiple) {
      const selectedValues = typeof value === 'string' ? value.split(',') : value;
      onChange(selectedValues);
    } else {
      onChange(value);
    }
  };

  const formatPortOption = (port: Port) => {
    return port.sfp ?? `${port.slot}/${port.portNumber}`;
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={20} />
        <span>Carregando portas...</span>
      </Box>
    );
  }

  if (error) {
    return (
      <Box color="error.main">
        {error}
      </Box>
    );
  }

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      disabled={disabled || loading}
      required={required}
    >
      <InputLabel id="pon-ports-select-label">{label}</InputLabel>
      <Select
        labelId="pon-ports-select-label"
        id="pon-ports-select"
        multiple={multiple}
        value={value}
        label={label}
        onChange={handleChange}
        renderValue={(selected) => {
          if (renderValue) return renderValue(selected as string);
          if (multiple) {
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {(selected as string[]).map((val) => {
                  const port = ports.find((p) => p.id.toString() === val);
                  return <Chip key={val} label={port ? formatPortOption(port) : val} size="small" />;
                })}
              </Box>
            );
          } else {
            const port = ports.find((p) => p.id.toString() === selected);
            return port ? formatPortOption(port) : '';
          }
        }}
      >
        {ports.map((port) => (
          <MenuItem key={port.id} value={port.id.toString()}>
            {menuItemLabel ? menuItemLabel(port) : formatPortOption(port)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default PonPortsOltSelect; 